import React, { useEffect, useMemo, useState } from 'react';

type ImageCandidate = string | React.ReactNode;

type FallbackImageSource = {
  kind: 'image';
  src: string;
};

type FallbackImageNode = {
  kind: 'node';
  node: React.ReactNode | null;
};

type ResolvedDisplay = FallbackImageSource | FallbackImageNode;

export type FallbackImageProps = {
  src?: string;
  fallbackImage?: ImageCandidate | ImageCandidate[];
  initialImage?: ImageCandidate;
} & Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'>;

const normalizeCandidates = (candidate?: ImageCandidate | ImageCandidate[]) => {
  if (Array.isArray(candidate)) return candidate;
  if (!candidate) return [] as ImageCandidate[];
  return [candidate];
};

const FallbackImage: React.FC<FallbackImageProps> = ({
  src,
  fallbackImage,
  initialImage,
  className,
  style,
  alt,
  ...rest
}) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(() => (src ? 'loading' : 'error'));
  const [display, setDisplay] = useState<ResolvedDisplay>(() => ({
    kind: 'node',
    node: initialImage ?? null,
  }));

  const candidates = useMemo(() => {
    const list: ImageCandidate[] = [];
    if (src) list.push(src);
    return list.concat(normalizeCandidates(fallbackImage));
  }, [fallbackImage, src]);

  useEffect(() => {
    let isActive = true;

    if (!src) {
      setStatus('error');
      setDisplay({
        kind: 'node',
        node: Array.isArray(fallbackImage) ? (fallbackImage[0] ?? null) : (fallbackImage ?? null),
      });
      return () => {
        isActive = false;
      };
    }

    setStatus('loading');
    setDisplay({ kind: 'node', node: initialImage ?? null });

    const queue = candidates;

    const tryLoad = (index: number) => {
      const candidate = queue[index];
      if (!isActive) return;
      if (!candidate) {
        setStatus('error');
        setDisplay({ kind: 'node', node: null });
        return;
      }

      if (typeof candidate !== 'string') {
        setStatus('loaded');
        setDisplay({ kind: 'node', node: candidate });
        return;
      }

      const img = new Image();
      img.onload = () => {
        if (!isActive) return;
        setStatus('loaded');
        setDisplay({ kind: 'image', src: candidate });
      };
      img.onerror = () => {
        if (!isActive) return;
        tryLoad(index + 1);
      };
      img.src = candidate;
    };

    tryLoad(0);

    return () => {
      isActive = false;
    };
  }, [candidates, fallbackImage, initialImage, src]);

  const applyClassAndStyle = (node: ImageCandidate | null | undefined): React.ReactNode => {
    if (!node) return null;
    if (typeof node === 'string') {
      return <img {...rest} alt={alt} className={className} src={node} style={style} />;
    }
    if (React.isValidElement(node)) {
      return (
        <span className={className} style={style}>
          {node}
        </span>
      );
    }
    return node;
  };

  if (display.kind === 'image') {
    return <img {...rest} alt={alt} className={className} src={display.src} style={style} />;
  }

  if (status === 'loading' && initialImage) {
    return <>{applyClassAndStyle(initialImage)}</>;
  }

  if (display.kind === 'node') {
    return <>{applyClassAndStyle(display.node)}</>;
  }

  return null;
};

export default FallbackImage;
